import type { APIGame, APIGameAnalysisOverview } from "@rosette/ts/src/business/api/api_schema.js";
import type { QueryClient } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import type { GetGameResponse } from "@/app/(api)/api/getGame/getGame.js";
import { getGame } from "@/app/(api)/api/getGame/getGame.js";
import { Ruleset } from "@rosette/ts/src/business/game/Ruleset.js";


export function useGame(gameID: string | null, refetchIfAnalysisUnavailable?: boolean): {
    game: APIGame | null | undefined;
    gameLoading: boolean;
    gameError: Error | null;
} {
    const { data, isLoading, error } = useQuery({
        queryKey: ["game", gameID],
        queryFn: () => getGame(gameID),
        staleTime: 60 * 60 * 1000,
        refetchInterval: (refetchIfAnalysisUnavailable ? (query) => {
            const game: APIGame | null | undefined = query.state.data?.game;
            if (!game || game.analysis)
                return undefined;

            // Check if we would expect an analysis for this game.
            const ruleset = Ruleset.getByID(game.rulesetID);
            if (ruleset !== Ruleset.FINKEL
                && ruleset !== Ruleset.BLITZ
                && ruleset !== Ruleset.MASTERS) {
                return undefined;
            }

            return Math.max(
                (Date.now() - game.createdAt.getTime()) / 5,
                3000,
            );
        } : undefined),
    });
    return {
        game: data?.game,
        gameLoading: isLoading,
        gameError: error,
    };
}


export function useGameAnalysis(gameID: string | null): {
    game: APIGame | null | undefined;
    analysis: APIGameAnalysisOverview | null | undefined;
    analysisLoading: boolean;
    analysisError: Error | null;
} {
    const { game, gameLoading, gameError } = useGame(gameID, true);
    return {
        game,
        analysis: game?.analysis,
        analysisLoading: gameLoading,
        analysisError: gameError,
    };
}


export function cacheGame(queryClient: QueryClient, game: APIGame) {
    const queryKey = ["game", game.publicID];
    const existing = queryClient.getQueryData(queryKey);
    if (existing !== undefined)
        return;

    const data: GetGameResponse = { game };
    queryClient.setQueryData(queryKey, data);
    if (!data.game?.contents) {
        void queryClient.invalidateQueries({ queryKey });
    }
}


export function invalidateGame(queryClient: QueryClient, gameID: string) {
    queryClient.removeQueries({ queryKey: ["game", gameID] });
}
